import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import { Heart, ArrowLeft, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Layout from '@/components/Layout';
import { trackEvent } from '@/lib/analytics';

const FLOATING_HEARTS = [
  { x: '10%', delay: 0, duration: 4.2 },
  { x: '25%', delay: 1.1, duration: 3.8 },
  { x: '50%', delay: 0.6, duration: 4.6 },
  { x: '70%', delay: 1.8, duration: 3.5 },
  { x: '88%', delay: 0.3, duration: 4.0 },
];

const FloatingHeart = ({ x, delay, duration }: { x: string; delay: number; duration: number }) => {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) return null;
  return (
    <motion.div
      className="absolute bottom-0 pointer-events-none select-none"
      style={{ left: x }}
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: [0, 0.35, 0], y: -160 }}
      transition={{ delay, duration, repeat: Infinity, repeatDelay: duration * 1.5, ease: 'easeOut' }}
    >
      <Heart className="w-4 h-4 text-accent fill-accent/40" />
    </motion.div>
  );
};

const handleTipClick = () => {
  trackEvent('tip_jar_clicked');
};

const TipJar = () => {
  const heartControls = useAnimationControls();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced) return;
    heartControls.start({
      scale: [1, 1.18, 1],
      transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
    });
  }, [heartControls, prefersReduced]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-10 max-w-xl relative">
        {/* Background glow */}
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background: 'radial-gradient(ellipse 70% 40% at 50% 20%, hsl(24 95% 53% / 0.08) 0%, transparent 70%)',
          }}
        />

        {/* Floating hearts */}
        <div className="absolute inset-x-0 bottom-24 h-40 overflow-hidden pointer-events-none">
          {FLOATING_HEARTS.map((h, i) => (
            <FloatingHeart key={i} {...h} />
          ))}
        </div>

        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-8 min-h-[44px] cursor-pointer transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          חזרה
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={heartControls}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 border border-accent/30 mb-5"
          >
            <Heart className="w-8 h-8 text-accent fill-accent/60" />
          </motion.div>

          <h1 className="font-leon text-4xl font-bold tracking-tight mb-4 text-foreground">
            תמיכה בפרויקט
          </h1>
          <div className="text-muted-foreground leading-relaxed text-base max-w-sm mx-auto space-y-3">
            <p>Bracket Run הוא פרויקט חינמי שנבנה בשביל הכיף - חברים, פלייאוף, ניחושים ותחרות קטנה על הדרך.</p>
            <p>אין פה מודל עסקי או פרסומות.</p>
            <p>נהניתם מהטורניר? אפשר לפרגן בתרומה קטנה לעזור לנו להמשיך לשפר את הפרויקט.</p>
          </div>
        </motion.div>

        {/* PayBox CTA */}
        <motion.a
          href="https://links.payboxapp.com/myIkrI4DWUb"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleTipClick}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          whileHover={{ y: -3, transition: { duration: 0.2 } }}
          className="group relative flex items-center justify-between gap-4 bg-card border border-accent/40 rounded-2xl p-6 cursor-pointer transition-all duration-200 hover:border-accent hover:shadow-[0_0_32px_hsl(24_95%_53%_/0.18)]"
        >
          {/* Orange left stripe */}
          <div className="absolute right-0 top-4 bottom-4 w-1 rounded-full bg-accent/70 group-hover:bg-accent transition-colors duration-200" />

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0 overflow-hidden group-hover:bg-accent/20 transition-colors duration-200">
              <img src="/images/paybox-logo.png" alt="PayBox" className="w-9 h-9 rounded-md" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">PayBox</h3>
            </div>
          </div>

          <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors duration-200 shrink-0" />
        </motion.a>

        {/* Footer thanks */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground mt-10"
        >
          תודה לכל מי שמשתתף, מפרגן ועוזר לפרויקט להתקדם 🧡
        </motion.p>
      </div>
    </Layout>
  );
};

export default TipJar;
